import { APIError } from '@/lib/api/authenticatedFetch';

export interface ApifyRun {
  id: string;
  actId: string;
  actorTaskId: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  buildId?: string;
  buildNumber?: string;
  usageTotalUsd?: number;
}

export interface ApifyRunsResponse {
  data: {
    total: number;
    count: number;
    items: ApifyRun[];
  };
}

export interface ApifyRunDetailsResponse {
  data: ApifyRun;
}

/**
 * Check Apify for active runs for a specific actor task
 * @param actorTaskName - The Apify actor task name (e.g., "kenkebiai/cargurus-ford-fl-ft-myers")
 * @param apifyToken - Apify API token (optional, will use env var if not provided)
 * @returns Array of active runs (status: RUNNING, QUEUED, or READY)
 */
export async function checkApifyActiveRuns(
  actorTaskName: string,
  apifyToken?: string
): Promise<ApifyRun[]> {
  // Use provided token, or try environment variable, or use the default token from the endpoint
  const token =
    apifyToken ||
    import.meta.env['VITE_APIFY_API_TOKEN'] ||
    'apify_api_4gMylilEELc1qzUBck1nAdI0PqbcA62FWDWB';

  if (!token) {
    return [];
  }

  try {
    // Convert task name from "kenkebiai/cargurus-ford-fl-ft-myers" to "kenkebiai~cargurus-ford-fl-ft-myers"
    // Apify API uses ~ as separator in the URL path
    const taskNameForUrl = actorTaskName.replace(/\//g, '~');

    // Query Apify API for runs with this actor task name
    // Endpoint format: /v2/actor-tasks/{actorTaskName}/runs
    const url = `https://api.apify.com/v2/actor-tasks/${taskNameForUrl}/runs?token=${token}&limit=20&desc=true`;

    const response = await fetch(url);

    if (!response.ok) {
      let errorMessage = `Failed to query Apify API: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error?.message || errorData.message || errorMessage;
      } catch {
        // If we can't parse error, use status text
      }
      throw new APIError(errorMessage, response.status, response.statusText);
    }

    const data: ApifyRunsResponse = await response.json();

    // Filter to only return truly active runs (RUNNING, QUEUED, or READY)
    // READY might be a transient state, but we'll include it
    return data.data.items.filter(
      run => run.status === 'RUNNING' || run.status === 'QUEUED' || run.status === 'READY'
    );
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    // Return empty array on non-API errors (network issues, etc.)
    return [];
  }
}

/**
 * Check Apify for active runs for multiple actor task names
 * @param actorTaskNames - Array of Apify actor task names (e.g., "kenkebiai/cargurus-ford-fl-ft-myers")
 * @param apifyToken - Apify API token (optional)
 * @returns Map of actorTaskName to array of active runs
 */
export async function checkMultipleApifyActiveRuns(
  actorTaskNames: string[],
  apifyToken?: string
): Promise<Map<string, ApifyRun[]>> {
  const results = new Map<string, ApifyRun[]>();

  // Check each task name in parallel
  const checks = actorTaskNames.map(async taskName => {
    try {
      const runs = await checkApifyActiveRuns(taskName, apifyToken);
      return { taskName, runs };
    } catch (error) {
      return { taskName, runs: [] };
    }
  });

  const allResults = await Promise.all(checks);

  allResults.forEach(({ taskName, runs }) => {
    results.set(taskName, runs);
  });

  return results;
}

/**
 * Get details of a specific Apify run
 * @param runId - The Apify run ID
 * @param apifyToken - Apify API token (optional)
 * @returns Run details or null if not found
 */
export async function getApifyRunDetails(
  runId: string,
  apifyToken?: string
): Promise<ApifyRun | null> {
  const token =
    apifyToken ||
    import.meta.env['VITE_APIFY_API_TOKEN'] ||
    'apify_api_4gMylilEELc1qzUBck1nAdI0PqbcA62FWDWB';

  if (!token) {
    return null;
  }

  try {
    const url = `https://api.apify.com/v2/actor-runs/${runId}?token=${token}`;
    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new APIError(
        `Failed to get Apify run details: ${response.statusText}`,
        response.status,
        response.statusText
      );
    }

    const data: ApifyRunDetailsResponse = await response.json();
    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    // Return null on non-API errors
    return null;
  }
}

/**
 * Get Apify run details for multiple run IDs
 * @param runIds - Array of Apify run IDs
 * @param apifyToken - Apify API token (optional)
 * @returns Map of runId to run details
 */
export async function getMultipleApifyRunDetails(
  runIds: string[],
  apifyToken?: string
): Promise<Map<string, ApifyRun>> {
  const results = new Map<string, ApifyRun>();

  // Get details for each run in parallel
  const checks = runIds.map(async runId => {
    try {
      const runDetails = await getApifyRunDetails(runId, apifyToken);
      return { runId, runDetails };
    } catch (error) {
      return { runId, runDetails: null };
    }
  });

  const allResults = await Promise.all(checks);

  allResults.forEach(({ runId, runDetails }) => {
    if (runDetails) {
      results.set(runId, runDetails);
    }
  });

  return results;
}
